import React, { useState } from 'react';
import { Button, Dialog, Group } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { useLocalization } from '~/app/shared/components/l10n/l10n';
import { useHasGraveyardFeaturePermission } from '~/app/shared/util/utilities/accessCheck';
import { Field, Form } from '../../../../../shared/components/Form';
import { CreateSalesDocumentLinesFromProductCollectionModel } from '../../../../../../types/internal-api';
import ProductCollectionCombobox from '../../../../../shared/components/ui/ProductCollectionCombobox';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';

const validationSchema = Yup.object({
    productCollectionId: Yup.number().required(t`Required`),
    salesDocumentId: Yup.number().required(t`Required`),
    quantity: Yup.number()
        .required(t`Required`)
        .min(1, t`The number must be over ${0}.`),
    addCollectionTitleLine: Yup.boolean(),
    addCollectionTotalPriceLine: Yup.boolean(),
});

export default function AddProductCollectionDialog() {
    const textsLoading = useLocalization(textIds);
    const navigate = useNavigate();
    const location = useLocation();
    const [requestLoading, setRequestLoading] = useState<boolean>(false);
    const [initialData] = useState<Partial<CreateSalesDocumentLinesFromProductCollectionModel>>({
        salesDocumentId: location.state?.draftId,
        productCollectionId: undefined,
        quantity: 1,
        addCollectionTitleLine: false,
        addCollectionTotalPriceLine: false,
    });
    const hasGraveyardFeaturePermission = useHasGraveyardFeaturePermission();

    if (textsLoading || !hasGraveyardFeaturePermission) {
        return null;
    }

    const onClose = () => {
        navigate(location.pathname);
    };
    const submit = async (values: CreateSalesDocumentLinesFromProductCollectionModel) => {
        setRequestLoading(true);
        try {
            const response = await internalApiClient.post(`/sales-document-line/create-from-product-collection`, values);
            setRequestLoading(false);
            window.top?.Econ.frames.content.location.reload();
            return Promise.resolve(response);
        } catch (error) {
            setRequestLoading(false);
            return Promise.reject(error);
        }
    };

    return (
        <Dialog size="dialog" open={location.hash === '#add-product-collection'} onClose={onClose}>
            <Dialog.Content aria-label="add-product-collection-dialog">
                <Form onSubmit={submit} initialData={initialData} validationSchema={validationSchema} onSubmitted={onClose}>
                    <Dialog.Title>{t`Select product collection`}</Dialog.Title>

                    <Field.Combobox
                        as={ProductCollectionCombobox}
                        name="productCollectionId"
                        label={t`Product collection`}
                        required
                    />
                    <Field.Number name="quantity" required label={t`Quantity factor`} />
                    <span className="font-bold">
                        <Trans>Display:</Trans>
                    </span>
                    <div className="flex mt-4">
                        <Field.Checkbox
                            name="addCollectionTitleLine"
                            text={t`Show collection title`}
                            hideLabel
                            fieldClassName="mr-4"
                        />
                        <Field.Checkbox name="addCollectionTotalPriceLine" text={t`Show collection total price`} hideLabel />
                    </div>
                    <Dialog.Footer>
                        <Group>
                            <Button onClick={onClose}>
                                <Trans>Close</Trans>
                            </Button>
                            <Button type="submit" appearance="primary" disabled={requestLoading}>
                                <Trans>OK</Trans>
                            </Button>
                        </Group>
                    </Dialog.Footer>
                </Form>
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'ERROR_CODE_CreateFromProductCollectionProductCollectionDoesNotExist',
    'ERROR_CODE_CreateFromProductCollectionProductCollectionIsEmpty',
];
